import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowLeft, FiArrowRight, FiCalendar, FiClock, FiShare2, FiUser } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const BlogPost = () => {
  const { id } = useParams();

  // In a real app, you'd fetch this data based on the ID
  const blogPosts = {
    1: {
      id: 1,
      title: "The Future of AI in Enterprise: Transforming Business Operations",
      excerpt: "Discover how artificial intelligence is revolutionizing enterprise operations, from automated workflows to predictive analytics that drive strategic decision-making.",
      author: "Sarah Johnson",
      authorBio: "Senior AI Consultant with 10+ years of experience in enterprise AI implementations. Sarah specializes in machine learning, data analytics, and digital transformation strategies.",
      date: "December 15, 2024",
      readTime: "8 min read",
      category: "Artificial Intelligence",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop&crop=entropy&auto=format",
      gradient: "from-purple-500 to-pink-600",
      content: `
        <p>Artificial Intelligence is no longer a futuristic concept—it's here, and it's transforming how enterprises operate across every industry. From automating routine tasks to providing deep insights through predictive analytics, AI is becoming the backbone of modern business operations.</p>

        <h2>The Current State of Enterprise AI</h2>
        <p>Today's enterprises are adopting AI at an unprecedented rate. According to recent studies, over 80% of Fortune 500 companies have integrated some form of AI into their operations. This rapid adoption is driven by the tangible benefits AI provides:</p>

        <ul>
          <li><strong>Operational Efficiency:</strong> AI automates repetitive tasks, allowing employees to focus on strategic work</li>
          <li><strong>Data-Driven Decisions:</strong> Machine learning algorithms analyze vast datasets to provide actionable insights</li>
          <li><strong>Customer Experience:</strong> Personalized recommendations and chatbots enhance customer interactions</li>
          <li><strong>Cost Reduction:</strong> Automated processes reduce operational costs by up to 30%</li>
        </ul>

        <h2>Key AI Applications in Enterprise</h2>
        <p>Let's explore the most impactful AI applications that are reshaping enterprise operations:</p>

        <h3>1. Predictive Analytics</h3>
        <p>Predictive analytics uses historical data to forecast future trends, helping businesses make informed decisions. In supply chain management, predictive models can anticipate demand fluctuations, optimize inventory levels, and prevent stockouts.</p>

        <h3>2. Process Automation</h3>
        <p>Robotic Process Automation (RPA) combined with AI creates intelligent automation systems that can handle complex, rule-based tasks. This includes everything from invoice processing to customer service interactions.</p>

        <h3>3. Natural Language Processing</h3>
        <p>NLP enables machines to understand and respond to human language, powering applications like sentiment analysis, document processing, and intelligent chatbots that can handle sophisticated customer queries.</p>

        <h2>Implementation Strategies</h2>
        <p>Successfully implementing AI in enterprise requires a strategic approach:</p>

        <ol>
          <li><strong>Start Small:</strong> Begin with pilot projects that address specific pain points</li>
          <li><strong>Data Foundation:</strong> Ensure high-quality, accessible data infrastructure</li>
          <li><strong>Skill Development:</strong> Invest in training existing employees or hiring AI specialists</li>
          <li><strong>Ethical Considerations:</strong> Establish governance frameworks for responsible AI use</li>
        </ol>

        <h2>Challenges and Solutions</h2>
        <p>While AI offers tremendous opportunities, enterprises face several challenges:</p>

        <p><strong>Data Quality:</strong> Poor data quality can lead to inaccurate AI predictions. Solution: Implement robust data governance and cleaning processes.</p>

        <p><strong>Integration Complexity:</strong> Integrating AI with existing systems can be challenging. Solution: Use API-first approaches and microservices architecture.</p>

        <p><strong>Change Management:</strong> Employee resistance to AI adoption is common. Solution: Focus on AI as an augmentation tool rather than replacement.</p>

        <h2>Looking Ahead</h2>
        <p>The future of enterprise AI is bright, with emerging trends like:</p>

        <ul>
          <li>Federated learning for privacy-preserving AI</li>
          <li>Edge AI for real-time processing</li>
          <li>Explainable AI for better transparency</li>
          <li>AI-powered cybersecurity solutions</li>
        </ul>

        <p>As AI technology continues to evolve, enterprises that embrace these innovations will gain significant competitive advantages. The key is to start now, learn continuously, and scale thoughtfully.</p>

        <h2>Conclusion</h2>
        <p>AI is not just transforming individual processes—it's reshaping entire business models. Companies that successfully integrate AI into their operations will be better positioned to adapt to changing market conditions, serve customers more effectively, and drive sustainable growth.</p>

        <p>The journey to AI transformation begins with understanding your organization's unique needs and taking the first step toward implementation. With the right strategy, tools, and partnerships, your enterprise can harness the full power of artificial intelligence.</p>
      `,
      tags: ["AI", "Machine Learning", "Enterprise", "Digital Transformation", "Automation"]
    }
    // You can add more blog posts here
  };

  const currentPost = blogPosts[id] || blogPosts[1]; // Fallback to first post if ID not found

  const relatedPosts = [
    {
      id: 2,
      title: "Cloud Migration Strategies: Best Practices for Modern Enterprises",
      category: "Cloud Infrastructure",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=250&fit=crop&crop=entropy&auto=format",
      readTime: "6 min read"
    },
    {
      id: 3,
      title: "Cybersecurity in 2024: Emerging Threats and Defense Strategies",
      category: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&h=250&fit=crop&crop=entropy&auto=format",
      readTime: "10 min read"
    },
    {
      id: 4,
      title: "Building Agile Development Teams: A Complete Guide to Success",
      category: "Project Management",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=entropy&auto=format",
      readTime: "7 min read"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-20 relative overflow-hidden">
        <div className="relative h-96 md:h-[500px]">
          <img
            src={currentPost.image}
            alt={currentPost.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-4"
              >
                <div className="flex items-center space-x-4 text-white/80 text-sm font-body">
                  <Link 
                    to="/blogs"
                    className="flex items-center space-x-2 hover:text-white transition-colors duration-300"
                  >
                    <FiArrowLeft size={16} />
                    <span>Back to Blog</span>
                  </Link>
                  <span>•</span>
                  <span className={`px-3 py-1 bg-gradient-to-r ${currentPost.gradient} rounded-full font-accent tracking-wide`}>
                    {currentPost.category}
                  </span>
                </div>
                
                <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight font-display tracking-tight">
                  {currentPost.title}
                </h1>
                
                <div className="flex items-center space-x-6 text-white/80 text-sm font-body">
                  <div className="flex items-center space-x-2">
                    <FiUser size={16} />
                    <span>{currentPost.author}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FiCalendar size={16} />
                    <span>{currentPost.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FiClock size={16} />
                    <span>{currentPost.readTime}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <motion.article
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              {/* Article Body */}
              <div 
                className="prose prose-lg max-w-none prose-headings:font-display prose-headings:tracking-tight prose-p:font-body prose-p:leading-relaxed prose-li:font-body prose-strong:font-heading prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                dangerouslySetInnerHTML={{ __html: currentPost.content }}
              />

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading tracking-wide">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {currentPost.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors duration-300 cursor-pointer font-body"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Author Bio */}
              <div className="mt-12 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold font-display">
                      {currentPost.author.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading tracking-wide">
                      {currentPost.author}
                    </h3>
                    <p className="text-gray-600 font-body leading-relaxed">
                      {currentPost.authorBio}
                    </p>
                  </div>
                </div>
              </div>
            </motion.article>

            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-1 space-y-8"
            >
              {/* Share */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading tracking-wide">
                  Share This Article
                </h3>
                <button className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 font-accent tracking-wide">
                  <FiShare2 size={18} />
                  <span>Share</span>
                </button>
              </div>

              {/* Table of Contents (you can auto-generate this from headings) */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading tracking-wide">
                  Table of Contents
                </h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-body">Current State of Enterprise AI</a></li>
                  <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-body">Key AI Applications</a></li>
                  <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-body">Implementation Strategies</a></li>
                  <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-body">Challenges and Solutions</a></li>
                  <li><a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-body">Looking Ahead</a></li>
                </ul>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-display tracking-tight">
              Related Articles
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-body font-light tracking-wide">
              Continue exploring our latest insights and expert perspectives
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {relatedPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group cursor-pointer"
              >
                <Link to={`/blog/${post.id}`}>
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-xs text-blue-600 font-medium mb-2 block font-accent tracking-wide">
                        {post.category}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 font-heading tracking-wide leading-tight">
                        {post.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 font-body">{post.readTime}</span>
                        <motion.div
                          whileHover={{ x: 5 }}
                          className="text-blue-600 group-hover:text-blue-700"
                        >
                          <FiArrowRight size={18} />
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost; 